/**
 * Component Metadata Utilities
 *
 * Shared utilities for correspondence component display.
 * Consolidates duplicate logic from ComponentItem.tsx and PackagePreviewItem.tsx
 */

import type { CorrespondenceComponentSchema } from '../types';

// ============================================================================
// Component Title
// ============================================================================

/**
 * Get display title for a correspondence component.
 * Handles all component types with appropriate fallbacks.
 */
export function getComponentTitle(
  component: CorrespondenceComponentSchema,
): string {
  switch (component.component_type) {
    case 'template':
      return (
        component.template?.display_name ||
        component.template?.name ||
        component.template?.category ||
        'Untitled Template'
      );

    case 'global_template':
      return component.global_template_key || 'Global Template';

    case 'merge_document': {
      const sourceName =
        component.blob?.original_filename || component.blob?.name;
      const templateName =
        component.template?.display_name || component.template?.name;
      return sourceName || templateName || 'Merge Document';
    }

    case 'matter_blob':
      return (
        component.blob?.name ||
        component.blob?.original_filename ||
        'Untitled Document'
      );

    case 'static_document':
      return component.static_document_id || 'Static Document';

    case 'external_file':
      return component.external_url || 'External File';

    case 'generated_content':
      return component.generated_content_key || 'Generated Content';

    default:
      return 'Unknown Component';
  }
}
